/**
 * Single source of truth for analytics consent state.
 *
 * The shell, the tag manager loader and the analytics emitters all derive their
 * behavior from `ConsentState`. The criterion for analytics-bearing scripts is
 * **explicit grant**: rejected and unknown states block script loading.
 */

export type ConsentState = 'granted' | 'rejected' | 'unknown'

export const CONSENT_COOKIE_NAME = 'cookieConsent'

export function parseConsentCookieValue(
	value: string | undefined | null,
): ConsentState {
	if (value === 'true') return 'granted'
	if (value === 'false') return 'rejected'
	return 'unknown'
}

export function extractConsentFromCookieHeader(
	cookieHeader: string | null | undefined,
): ConsentState {
	if (!cookieHeader) return 'unknown'
	const value = cookieHeader
		.split(';')
		.map((cookie) => cookie.trim().split('='))
		.find((parts) => parts[0] === CONSENT_COOKIE_NAME)?.[1]
	return parseConsentCookieValue(value)
}

export function extractConsent(request: Request): ConsentState {
	return extractConsentFromCookieHeader(request.headers.get('Cookie'))
}

export function isAnalyticsAllowed(state: ConsentState): boolean {
	return state === 'granted'
}

/**
 * Whether the Google Tag Manager script (`gtm.js`) is allowed to be requested.
 *
 * Decision recorded in PRD #227: we block the script load until consent is
 * explicitly granted, rather than relying on Consent Mode v2 with `denied`
 * defaults. The criterion is the same as `isAnalyticsAllowed`, but kept as a
 * separate function to leave room for future divergence (e.g. strictly
 * necessary tags).
 */
export function shouldLoadTagManager(state: ConsentState): boolean {
	return state === 'granted'
}
